import { Loader2 } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DockDoorSearchBar, DockDoorTable, normalizeStatus } from '@/components/dockDoors';
import { useToast } from '@/components/ui';
import { dockDoorApi } from '@/service/dockDoorService';
import { useAppSelector, selectSettings } from '@/store';

import type { LocationDetail } from '@/service/types/dockDoorTypes';

const LOCATION_TYPE = 'Door';

// ── Auto-refresh interval parser ──
// Matches Xamarin AutoRefreshTimerModel.DockDoorListPage
function parseAutoRefreshInterval(autoRefreshScreensJson: string | undefined): number {
  if (!autoRefreshScreensJson) return 0;
  try {
    const screens = JSON.parse(autoRefreshScreensJson) as Record<string, number>;
    const intervalSec = screens['DockDoorListPage'] ?? 0;
    return intervalSec > 0 ? intervalSec * 1000 : 0;
  } catch {
    return 0;
  }
}

// ── Main Page Component ── Matches Xamarin DockDoorsPageViewModel

export function DockDoorsPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();

  // ── ACL permission — Matches Xamarin ConfigInfo.Settings.AclDockDoorStatusChange ──
  const settings = useAppSelector(selectSettings);
  const hasPermission = settings?.ACL_DOCK_DOOR_STATUS_CHANGE === true;
  const autoRefreshScreens = settings?.AUTO_REFRESH_SCREENS;

  const [locations, setLocations] = useState<LocationDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [updatingLocation, setUpdatingLocation] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<LocationDetail | null>(null);

  // Auto-refresh control — Matches Xamarin CanRunRefreshTimer
  const canRefreshRef = useRef(true);

  // ── Fetch dock door locations — Matches Xamarin ShowLocationList() ──

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await dockDoorApi.getLocationList(LOCATION_TYPE);
      setLocations(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.error('[DockDoors] Failed to fetch locations:', message, err);
      setError(t('dockDoors.fetchFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Silent refetch for auto-refresh (no loading spinner)
  const silentRefetch = useCallback(async () => {
    try {
      const data = await dockDoorApi.getLocationList(LOCATION_TYPE);
      setLocations(data);
    } catch {
      // Silent fail on auto-refresh — keeps existing data visible
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations]);

  // ── Auto-refresh timer — Matches Xamarin AutoRefreshTimer() ──
  useEffect(() => {
    const intervalMs = parseAutoRefreshInterval(autoRefreshScreens);
    if (intervalMs <= 0) return;

    canRefreshRef.current = true;

    const timer = setInterval(() => {
      if (canRefreshRef.current) {
        void silentRefetch();
      }
    }, intervalMs);

    return () => {
      canRefreshRef.current = false;
      clearInterval(timer);
    };
  }, [autoRefreshScreens, silentRefetch]);

  // ── Search filtering — Matches Xamarin SearchCommandHandler() ──
  // Filters by Location OR LocStatus (with "Open" → "Available" mapping)

  const filteredLocations = locations.filter((loc) => {
    if (!searchText) return true;
    const query = searchText.toUpperCase();
    return (
      loc.Location.toUpperCase().includes(query) ||
      normalizeStatus(loc.LocStatus).toUpperCase().includes(query)
    );
  });

  // ── Status toggle handlers — Matches Xamarin SelectedStatusCommandHandler ──

  const handleToggleStatus = useCallback(
    (location: LocationDetail) => {
      const normalized = normalizeStatus(location.LocStatus);

      // Guard: In Use and Pending In Use are never toggleable
      if (normalized === 'In Use (Full)' || normalized === 'Pending In Use') return;
      // Guard: must have ACL permission
      if (!hasPermission) return;

      setConfirmTarget(location);
    },
    [hasPermission]
  );

  // Matches Xamarin: confirm → ShowLoading → UpdateLocationAsync → optimistic local update → toast
  const handleConfirmUpdate = useCallback(async () => {
    if (!confirmTarget) return;

    const normalized = normalizeStatus(confirmTarget.LocStatus);
    const newStatus = normalized === 'Available' ? 'Unavailable' : 'Available';

    setConfirmTarget(null);
    setUpdatingLocation(confirmTarget.Location);

    try {
      const response = await dockDoorApi.updateLocation(
        confirmTarget.Location,
        newStatus,
        '',
        LOCATION_TYPE
      );

      // Matches Xamarin: check response.Status case-insensitive for "true"
      if (response.Payload.Body.Status.toLowerCase() === 'true') {
        // Optimistic local update — Matches Xamarin: toggle LocStatus locally without refetch
        setLocations((prev) =>
          prev.map((loc) =>
            loc.Location === confirmTarget.Location ? { ...loc, LocStatus: newStatus } : loc
          )
        );
        showToast(t('dockDoors.locationUpdatedSuccess'));
      } else {
        showToast(response.Payload.Body.StatusMessage || t('dockDoors.fetchFailed'), {
          type: 'error',
        });
      }
    } catch {
      showToast(t('dockDoors.fetchFailed'), { type: 'error' });
    } finally {
      setUpdatingLocation(null);
    }
  }, [confirmTarget, showToast, t]);

  const handleCancelUpdate = useCallback(() => {
    setConfirmTarget(null);
  }, []);

  // ── Loading state ──

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#1357A0]" />
      </div>
    );
  }

  // ── Error state ──

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-12 text-center">
        <p className="text-sm font-poppins text-[#445E71]">{error}</p>
        <button
          type="button"
          onClick={() => {
            void fetchLocations();
          }}
          className="rounded-lg bg-[#1357A0] px-6 py-2 text-sm font-poppins font-medium text-white hover:bg-[#0e4a8a] transition-colors"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  // ── Main render — Matches Xamarin Grid(Row 0: SearchBar, Row 1: DataList or EmptyState) ──

  return (
    <div className="flex flex-col gap-[5px] pt-[5px]">
      <DockDoorSearchBar searchText={searchText} onSearchChange={setSearchText} />
      <DockDoorTable
        locations={filteredLocations}
        updatingLocation={updatingLocation}
        hasPermission={hasPermission}
        onToggleStatus={handleToggleStatus}
      />

      {/* Confirmation Dialog — Matches Xamarin ConfirmAsync("Are you sure...", "Update Status", OK, Cancel) */}
      {confirmTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-poppins font-bold text-[#243743]">
              {t('dockDoors.updateStatus')}
            </h3>
            <p className="mb-6 text-sm font-poppins text-[#445E71]">
              {t('dockDoors.confirmStatusChange')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelUpdate}
                className="rounded-lg border border-[#D2E3F0] px-5 py-2 text-sm font-poppins font-medium text-[#445E71] hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleConfirmUpdate();
                }}
                className="rounded-lg bg-[#1357A0] px-5 py-2 text-sm font-poppins font-medium text-white hover:bg-[#0e4a8a] transition-colors"
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
